/**
 * 이상한 방 - 참가자 줄 (여럿이, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제 - 관전) · 4.4절(원래 방 정원 1) · 3절(라운드 구조 - 체인저 1인)
 *            `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것)
 *            로드맵 M2("화면 위 참가자 줄: 이름·찾은 수·탄환 점·관전 표식·원래 방 안 표식. 색은 `--gh-*`만")
 *
 * ## 이 줄이 답하는 질문은 하나다 - "지금 누가 몇 개 찍었나"
 *
 * 여럿이에서 이상한 곳은 **한 번만** 찾힌다(선착순, 로드맵 2.6절). 그래서 남이 하나 찍는 순간 내 남은 몫이 줄고, 그 사실을
 * 방을 보는 눈을 떼지 않고 곁눈으로 알아야 한다. 이 줄은 좌석마다 한 칸이고, 칸에는 이름과 찾은 수가 크게, 상태는 표식
 * 셋(관전 · 원래 방에 · 다녀간 사람)으로 **글자**로 붙는다. 색만으로 상태를 말하지 않는다 - 색각 이상에서도 읽혀야 하고,
 * 무엇보다 이 게임의 색은 노랑 하나뿐이라 상태마다 색을 배정할 수가 없다.
 *
 * ## 계산하지 않는다
 *
 * 받은 줄(`PlayerStripRow[]`)을 그린다. 누가 관전인지·누가 원래 방 안인지·누가 다녀간 사람인지는 호스트가 뷰에 내린 값이고,
 * 뷰를 이 모양으로 옮기는 것은 `game/matchSessionView.buildPlayerStripView`(산술 0)다. 여기서 `foundCount`를 더하거나
 * "탄환이 0이면 관전"을 판단하기 시작하면 규칙이 화면에 두 벌째로 생긴다(`gameHud.ts`의 같은 규약).
 *
 * ## 예외 하나 - 내 별명
 *
 * 별명 한 줄만은 뷰에 없는 값을 읽는다(`ui/cosmeticVars.myNicknameLabel()`). 남의 사물함은 뷰에 오지 않으므로
 * (호스트가 뷰를 만들고 호스트는 계정 저장을 모른다 - `matchView.ts`) 별명은 **내 줄에만** 붙고, 그 값의 출처는
 * 모듈 수준 상태다. 선례는 `ui/settings.isSettingsOpen()`이다. 같은 입력으로 두 번 그려도 결과가 같지만,
 * `applyCosmetics`를 부르기 전과 후는 다르다는 뜻이다.
 *
 * ## 탄환 점은 내 칸에만 있다 - 그리고 기본 튜닝에서는 없다
 *
 * 남의 잔탄은 뷰에 오지 않는다(`matchView.PublicPlayer` - "잔탄·콤보·탄창은 없다, 그건 자기 것이다"). 그래서 `ammo`가 `null`인
 * 칸은 점을 그리지 않는다. 남에게 남은 것은 "관전" 표식 하나다 - 탄환이 다 떨어졌다는 사실은 방 전체에 가는 효과라서다.
 * 점의 상한은 HUD의 탄피 줄과 **같은 값**(`gameHud.AMMO_PIP_MAX` = 12)을 그대로 가져온다 - 한쪽만 올리면 HUD는 접고 줄은
 * 그리는 어긋남이 생긴다. 기본 시작 탄환은 16발이라 **기본 튜닝에서는 점이 없다**(HUD도 같은 이유로 숫자만 보인다). 점은
 * `?ammo=` 실험·낮은 단계 같은 12발 이하의 판에서만 선다. 줄에는 숫자 대체가 없다 - 큰 숫자는 HUD가 이미 말하고 있고,
 * 이 줄의 질문은 "누가 몇 개 찍었나"다.
 *
 * ## 프레임마다 불려도 싸야 한다
 *
 * 세션이 HUD와 같은 자리(`render`)에서 부른다. 칸은 좌석 id로 붙여 두고 좌석 목록이 바뀔 때만 다시 만들며, 나머지는
 * `setText`·`setAttr`·클래스 토글로 값이 같으면 DOM을 건드리지 않는다(`ui/dom.ts`의 계약).
 */

import { t } from '../i18n'

import { myNicknameLabel } from './cosmeticVars'
import { createElement, setAttr, setFlag, setHidden, setText } from './dom'
import { AMMO_PIP_MAX } from './gameHud'
import { overlayLayout } from './overlayLayout'

import './playerStrip.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 칸 하나. 값은 전부 호스트의 뷰에서 옮겨 온 것이다(`matchSessionView.buildPlayerStripView`). */
export type PlayerStripRow = Readonly<{
  id: string
  name: string
  isMe: boolean
  /**
   * 지금 자기 구획을 손대는 중인가(M11 - 바꾸기 페이즈에 아직 [다 했다]를 안 누른 사람).
   * 아직 찾은 것이 있을 수 없는 사람이라 찾은 수·탄환 점을 그리지 않고 표식만 붙는다.
   */
  isVisitor: boolean
  spectating: boolean
  inOrigin: boolean
  /** 내가 관전 중이고 이 사람의 화면을 보고 있는가(`SelfView.spectateTarget`). */
  watched: boolean
  foundCount: number
  /** 잔탄. 남의 것은 뷰에 없어 `null`이고, 그때는 점을 그리지 않는다. */
  ammo: number | null
  ammoMax: number
}>

export type PlayerStripView = Readonly<{ rows: readonly PlayerStripRow[] }>

export type PlayerStripHandle = Readonly<{
  root: HTMLElement
  /**
   * `null`이면 접는다 - 이 줄이 말할 것이 없는 페이즈다(`matchSessionView.buildPlayerStripView`의 `STRIP_PHASES`).
   * 페이즈 판단은 여기 없다. 구획 진행판(`ui/zoneProgress.ts`)과 **같은 규약**이라 왼쪽 열의 두 칸이 같은 문법으로 선다.
   */
  render: (view: PlayerStripView | null) => void
  /** 배치 모드·잠든 무대에서는 줄째 접는다(HUD의 `setVisible`과 같은 시점에 불린다). */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type PlayerStripOptions = Readonly<{ mount: HTMLElement }>

/* --- 칸 ------------------------------------------------------------------------ */

/**
 * 폰 세로의 압축 이름표(한두 글자).
 *
 * 여덟 칸이 한 줄에 서려면 칸 하나가 41px이라 이름이 들어갈 자리가 없다. 그렇다고 첫 글자만 남기면
 * "이웃 1"~"이웃 7"이 전부 `이`가 되어 여덟 칸이 같은 글자가 된다 - 압축한 것이 아니라 지운 것이다.
 * 그래서 **첫 글자 + 끝에 붙은 숫자**를 남긴다("이웃 3" → `이3`, "Neighbor 5" → `N5`, "나" → `나`).
 * 숫자가 사람을 가르는 것은 이 게임의 기본 이름만이 아니라 사람이 짓는 이름에서도 흔한 규칙이고,
 * 숫자가 없으면 첫 글자 하나로 돌아간다.
 *
 * **그 한 글자가 겹치는 것은 이 함수가 막지 못한다** - 이름 하나만 보기 때문이다. 겹침은 줄 전체를 보는
 * `shortSeatLabels`가 좌석 순번으로 가른다. 화면에 쓰는 쪽은 언제나 그쪽이다.
 *
 * 이름 전체는 사라지지 않는다 - 칸의 `aria-label`과 결과 화면의 순위표가 그대로 들고 있다.
 */
export function shortSeatLabel(name: string): string {
  const trimmed = name.trim()
  if (trimmed === '') return '?'
  const head = Array.from(trimmed)[0] ?? '?'
  const tail = /(\d{1,2})\s*$/u.exec(trimmed)?.[1]
  return tail === undefined ? head : `${head}${tail}`
}

/**
 * 한 줄 전체의 압축 이름표. **겹치면 좌석 순번으로 가른다.**
 *
 * 이름 하나만 보고 줄이면 겹치는 것을 막을 방법이 없다 - `김민수`와 `김철수`는 둘 다 `김`이고,
 * `Neighbor 123`과 `Neighbor 223`은 둘 다 `N23`이다. 기본 이름(`이웃 1`~)에서는 안 나지만 로비에서
 * 사람이 직접 치는 이름에는 그 보장이 없고, **폰 세로에서는 이 두세 글자가 화면에 남은 유일한 식별자**라
 * (`playerStrip.css`가 이름·별명·"N 찾음"을 전부 접는다) 겹치는 순간 두 사람이 한 칸으로 읽힌다.
 *
 * 그래서 줄을 통째로 보고, 겹친 것만 `첫 글자 + 좌석 순번`으로 바꾼다. 순번은 줄에서 유일하므로 한 번의
 * 후처리로 끝난다. 안 겹치는 이름은 한 글자도 안 바뀐다 - 흔한 경우를 흔한 모습으로 둔다.
 */
export function shortSeatLabels(names: readonly string[]): readonly string[] {
  const labels = names.map((name) => shortSeatLabel(name))
  const seen = new Map<string, number>()
  for (const label of labels) seen.set(label, (seen.get(label) ?? 0) + 1)
  return labels.map((label, index) => {
    if ((seen.get(label) ?? 0) <= 1) return label
    const head = Array.from(names[index]?.trim() ?? '')[0] ?? '?'
    return `${head}${index + 1}`
  })
}

type RowNodes = Readonly<{
  root: HTMLLIElement
  name: HTMLElement
  /** 압축 이름표. **폰 세로에서만** 보인다(`playerStrip.css`) - 데스크톱에서는 이름 전체가 그대로 선다. */
  initial: HTMLElement
  /** 찾은 수의 숫자만. 같은 이유로 폰에서만 보인다("3 찾음"의 자리가 없다). */
  count: HTMLElement
  /** 별명. **내 칸에만** 글자가 온다(로드맵 M9) - 남의 사물함은 뷰에 없다(`ui/cosmeticVars.ts` 머리말). */
  nick: HTMLElement
  found: HTMLElement
  pips: HTMLElement
  pipNodes: HTMLElement[]
  spectate: HTMLElement
  origin: HTMLElement
  visitor: HTMLElement
  watched: HTMLElement
}>

function createRow(): RowNodes {
  const root = createElement('li', 'ps-row')
  const body = createElement('span', 'ps-row__body')
  const name = createElement('span', 'ps-row__name', '')
  const nick = createElement('span', 'ps-row__nick', '')
  /*
   * 압축 판 둘. 소리에서는 뺀다(`aria-hidden`) - 칸 전체를 한 문장으로 읽는 `aria-label`이 이미 있고,
   * 여기까지 읽히면 한 칸이 "이웃 3, 0개 찾음 · 이3 · 0"으로 세 번 들린다.
   */
  const initial = createElement('span', 'ps-row__initial', '')
  initial.setAttribute('aria-hidden', 'true')
  const count = createElement('span', 'ps-row__count', '')
  count.setAttribute('aria-hidden', 'true')
  const meta = createElement('span', 'ps-row__meta')
  const found = createElement('span', 'ps-row__found', '')
  const pips = createElement('span', 'ps-row__pips')
  pips.setAttribute('aria-hidden', 'true')
  const pipNodes: HTMLElement[] = []
  for (let index = 0; index < AMMO_PIP_MAX; index += 1) {
    const pip = createElement('i', 'ps-pip')
    pipNodes.push(pip)
    pips.append(pip)
  }
  const spectate = createElement('span', 'ps-badge is-spectate', '')
  const origin = createElement('span', 'ps-badge is-origin', '')
  const visitor = createElement('span', 'ps-badge is-visitor', '')
  const watched = createElement('span', 'ps-badge is-watched', '')
  meta.append(found, count, pips, visitor, origin, spectate, watched)
  body.append(initial, name, nick, meta)
  root.append(body)
  return { root, name, initial, count, nick, found, pips, pipNodes, spectate, origin, visitor, watched }
}

/** 스크린리더용 한 줄. 표식은 글자지만 칸 전체를 한 문장으로 읽는 쪽이 낫다. 관전은 찾은 수 대신 그 사실을 말한다. */
function rowAria(row: PlayerStripRow): string {
  if (row.isVisitor) return t('party.visitor')
  if (row.spectating) return t('spectate.rowAria', { name: row.name })
  return t('party.strip.rowAria', { name: row.name, found: row.foundCount })
}

function paintRow(nodes: RowNodes, row: PlayerStripRow, initial: string): void {
  setText(nodes.name, row.name)
  setText(nodes.initial, initial)
  /*
   * 별명은 이름의 일부가 아니라 옆에 붙는 한 줄이다(로드맵 M9). 없으면 노드째 접는다 - 빈 칸을 세워 두면
   * 칸 폭이 사람마다 달라져 "누가 몇 개 찍었나"를 읽는 눈이 한 번 더 이동한다.
   */
  const nickname = row.isMe ? myNicknameLabel() : null
  setHidden(nodes.nick, nickname === null)
  if (nickname !== null) setText(nodes.nick, nickname)
  setAttr(nodes.root, 'aria-label', rowAria(row))
  setFlag(nodes.root, 'is-me', row.isMe)
  setFlag(nodes.root, 'is-spectating', row.spectating)
  setFlag(nodes.root, 'is-visitor', row.isVisitor)
  setFlag(nodes.root, 'is-origin', row.inOrigin)

  // 다녀간 사람에게는 찾은 수가 없다 - 0을 적으면 "하나도 못 찾은 사람"으로 읽힌다.
  setHidden(nodes.found, row.isVisitor)
  setHidden(nodes.count, row.isVisitor)
  if (!row.isVisitor) {
    setText(nodes.found, t('party.strip.found', { count: row.foundCount }))
    // 폰의 압축 판은 같은 값의 숫자만이다. 사전을 거치지 않는 이유는 문구가 아니라 수치라서다(`juice.formatMultiplier`와 같다).
    setText(nodes.count, String(row.foundCount))
  }

  // 점은 내 칸(잔탄이 온 칸)에만, 상한 안에서만.
  const showPips = row.ammo !== null && !row.isVisitor && row.ammoMax > 0 && row.ammoMax <= AMMO_PIP_MAX
  setHidden(nodes.pips, !showPips)
  if (showPips) {
    const ammo = row.ammo ?? 0
    nodes.pipNodes.forEach((pip, index) => {
      setHidden(pip, index >= row.ammoMax)
      setFlag(pip, 'is-on', index < ammo)
    })
  }

  setHidden(nodes.visitor, !row.isVisitor)
  if (row.isVisitor) setText(nodes.visitor, t('party.visitor'))
  setHidden(nodes.origin, !row.inOrigin)
  if (row.inOrigin) setText(nodes.origin, t('party.inOrigin'))
  setHidden(nodes.spectate, !row.spectating)
  if (row.spectating) setText(nodes.spectate, t('spectate.badge'))
  setHidden(nodes.watched, !row.watched)
  if (row.watched) setText(nodes.watched, t('spectate.watching', { name: row.name }))
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 참가자 줄을 만들어 `mount`에 붙인다. 처음에는 **보이는 상태**이고 칸은 없다 - 첫 `render`가 좌석을 세운다.
 */
export function createPlayerStrip(options: PlayerStripOptions): PlayerStripHandle {
  const root = createElement('ul', 'ps-root')
  root.setAttribute('role', 'list')
  root.setAttribute('aria-label', t('party.strip.aria'))
  // 자리는 위 기둥이 정한다(`overlayLayout.ts`) - 이 줄이 몇 겹이 되든 아래 칸이 알아서 밀린다.
  const layout = overlayLayout(options.mount)
  layout.place(root, 'players')

  /** 좌석 id → 칸. 순서는 `rows`의 것이고, 목록이 달라질 때만 다시 세운다. */
  let rows = new Map<string, RowNodes>()
  let order: readonly string[] = []

  function sameOrder(next: readonly PlayerStripRow[]): boolean {
    if (next.length !== order.length) return false
    return next.every((row, index) => row.id === order[index])
  }

  function rebuild(next: readonly PlayerStripRow[]): void {
    const fresh = new Map<string, RowNodes>()
    for (const row of next) {
      // 남아 있는 좌석은 칸을 이어 쓴다 - 다시 만들면 전환 중인 클래스가 끊긴다.
      fresh.set(row.id, rows.get(row.id) ?? createRow())
    }
    rows = fresh
    order = next.map((row) => row.id)
    root.replaceChildren(...next.map((row) => fresh.get(row.id)?.root ?? createRow().root))
  }

  /** 바깥의 `setVisible`. `render(null)`과 별개의 스위치다 - 잠든 무대에 뷰가 와도 펴지지 않는다(진행판과 같은 규약). */
  let visible = true
  /*
   * 첫 뷰가 오기 전에는 **보이는 상태**다. 진행판(`zoneProgress`)은 반대로 접힌 채 시작하는데, 그쪽은 라운드의
   * 대부분에서 `null`을 받고 이 줄은 대부분에서 값을 받기 때문이다 - 기본값이 흔한 쪽이라야 무대가 서는
   * 첫 프레임에 줄이 깜빡이지 않는다.
   */
  let shown = true

  function sync(): void {
    setHidden(root, !(visible && shown))
  }

  return {
    root,

    render(view: PlayerStripView | null): void {
      shown = view !== null && view.rows.length > 0
      if (view !== null) {
        if (!sameOrder(view.rows)) rebuild(view.rows)
        // 언어가 바뀌면 줄 이름도 바뀐다. 프레임마다 값이 같으면 `setAttr`가 건드리지 않는다.
        setAttr(root, 'aria-label', t('party.strip.aria'))
        // 압축 이름표는 줄 전체를 보고 정한다 - 겹치면 좌석 순번으로 갈라야 하기 때문이다(`shortSeatLabels`).
        const initials = shortSeatLabels(view.rows.map((row) => row.name))
        view.rows.forEach((row, index) => {
          const nodes = rows.get(row.id)
          if (nodes !== undefined) paintRow(nodes, row, initials[index] ?? shortSeatLabel(row.name))
        })
      }
      sync()
      // 줄 수가 바뀌면 왼쪽 열이 잘리는지도 바뀐다. 관측기는 배경 탭에서 오지 않으므로 여기서 직접 알린다.
      layout.syncClip()
    },

    setVisible(next: boolean): void {
      visible = next
      sync()
      layout.syncClip()
    },

    dispose(): void {
      // 기둥에서 걷는다 - `remove()`만 하면 관측기가 떼어 낸 여덟 줄짜리 서브트리를 계속 붙든다.
      layout.unplace(root)
    },
  }
}
